/**
 * The "What's new at CMIT Solutions" slide of a review — see migration 132.
 *
 * Three groups — a product, a service, a person — each a list of lines, and
 * each line a name and a note about it. Asked for on the review form; the deck
 * shows the slide only when at least one line has something in it, and then
 * only the lines that do.
 */
export interface WhatsNewItem {
  name: string;
  detail: string;
}

export type WhatsNewKey = 'product' | 'service' | 'people';

export type WhatsNew = Record<WhatsNewKey, WhatsNewItem[]>;

export const WHATS_NEW_KEYS: WhatsNewKey[] = ['product', 'service', 'people'];

/** Lines a group can hold. A ceiling, not a target: the slide has room for a
 *  few under each heading, and the form starts every group with one. */
export const MAX_WHATS_NEW_LINES = 10;

const blank = (): WhatsNewItem => ({ name: '', detail: '' });

export const emptyWhatsNew = (): WhatsNew =>
  Object.fromEntries(WHATS_NEW_KEYS.map((k) => [k, [blank()]])) as WhatsNew;

const readItem = (value: unknown): WhatsNewItem => {
  const o = (value ?? {}) as Partial<WhatsNewItem>;
  return {
    name: typeof o.name === 'string' ? o.name : '',
    detail: typeof o.detail === 'string' ? o.detail : '',
  };
};

/** Never throws: a malformed value reads as nothing new, not a broken deck.
 *  A group saved before the slide took several lines is one object rather than
 *  a list, so it is read as a list of one. Every group has at least one line,
 *  so the form always has its boxes. */
export function parseWhatsNew(raw: string | null | undefined): WhatsNew {
  const out = emptyWhatsNew();
  if (!raw) return out;
  try {
    const parsed = JSON.parse(raw) as Partial<Record<WhatsNewKey, unknown>>;
    for (const key of WHATS_NEW_KEYS) {
      const value = parsed?.[key];
      const list = Array.isArray(value) ? value : value ? [value] : [];
      const items = list.slice(0, MAX_WHATS_NEW_LINES).map(readItem);
      if (items.length > 0) out[key] = items;
    }
  } catch {
    /* fall through to empty */
  }
  return out;
}

export const hasContent = (item: WhatsNewItem) =>
  item.name.trim() !== '' || item.detail.trim() !== '';

/** The filled-in lines of one group, trimmed. */
export const filledItems = (list: WhatsNewItem[]) =>
  list.map((i) => ({ name: i.name.trim(), detail: i.detail.trim() })).filter(hasContent);
